import React, { useState, useRef, useEffect } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const VerifyCode = ({ value, onChange, onComplete, disabled }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const inputBackground = isDark
    ? "rgba(45, 61, 69, 0.82)"
    : "rgba(239, 249, 253, 0.72)";
  const mutedText = isDark ? "rgba(255, 255, 255, 0.58)" : "#717d84";
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Initialize refs
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  useEffect(() => {
    // Update digits when value prop changes
    if (value && value.length <= 6) {
      const newDigits = value
        .split("")
        .concat(Array(6 - value.length).fill(""));
      setDigits(newDigits);
    }
  }, [value]);

  const handleFocus = (index) => {
    // Select all text when focusing
    setTimeout(() => {
      const inputElement = inputRefs.current[index]?.querySelector("input");
      if (inputElement) {
        inputElement.select();
      }
    }, 0);
  };

  const focusInput = (index) => {
    setTimeout(() => {
      const inputElement = inputRefs.current[index]?.querySelector("input");
      if (inputElement) {
        inputElement.focus();
      }
    }, 0);
  };

  const handleInputChange = (index, inputValue) => {
    if (disabled) return;

    // Handle paste
    if (inputValue.length > 1) {
      const pastedValue = inputValue.replace(/\D/g, "").slice(0, 6);
      const newDigits = [...digits];

      for (let i = 0; i < 6; i++) {
        newDigits[i] = pastedValue[i] || "";
      }

      setDigits(newDigits);
      const fullValue = newDigits.join("");
      onChange(fullValue);

      // Focus on the last filled input or next empty
      const nextIndex = Math.min(pastedValue.length, 5);
      focusInput(nextIndex);

      if (fullValue.length === 6) {
        onComplete(fullValue);
      }
      return;
    }

    // Handle single digit input
    if (/^\d$/.test(inputValue) || inputValue === "") {
      const newDigits = [...digits];
      newDigits[index] = inputValue;
      setDigits(newDigits);

      const fullValue = newDigits.join("");
      onChange(fullValue);

      // Auto-focus next input
      if (inputValue && index < 5) {
        focusInput(index + 1);
      }

      // Check if all digits are filled
      if (fullValue.length === 6) {
        onComplete(fullValue);
      }
    }
  };

  const handleKeyDown = (index, event) => {
    if (disabled) return;

    if (event.key === "Backspace" && !digits[index] && index > 0) {
      // Delete the previous digit and move cursor there
      const newDigits = [...digits];
      newDigits[index - 1] = "";
      setDigits(newDigits);

      const fullValue = newDigits.join("");
      onChange(fullValue);

      focusInput(index - 1);
    } else if (event.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    } else if (event.key === "ArrowRight" && index < 5) {
      focusInput(index + 1);
    }
  };

  return (
    <Box sx={{ textAlign: "center", mb: 3 }}>
      <Typography
        variant="h6"
        sx={{ mb: 1.5, color: "text.primary", fontWeight: 600 }}
      >
        Enter 6-digit verification code
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: mutedText }}>
        This is to verify the sign-in request is from your trusted hub device.
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mb: 2,
          flexWrap: "nowrap",
        }}
      >
        {digits.map((digit, index) => (
          <TextField
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            value={digit}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            inputProps={{
              maxLength: 6, // Allow paste of full 6 digits
              style: {
                textAlign: "center",
                fontSize: "1.5rem",
                fontWeight: "bold",
                padding: "12px 8px",
              },
            }}
            sx={{
              width: { xs: 44, sm: 50 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: inputBackground,
                transition:
                  "box-shadow 160ms ease, background-color 160ms ease",
                "& fieldset": {
                  border: 0,
                },
                "&:hover fieldset": {
                  border: 0,
                },
                "&.Mui-focused fieldset": {
                  border: 0,
                },
                "&.Mui-focused": {
                  boxShadow: `0 0 0 2px ${theme.palette.primary.main}24`,
                },
                "&.Mui-disabled": {
                  opacity: 0.7,
                },
              },
              "& input": {
                fontSize: "1.5rem",
                fontWeight: "bold",
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default VerifyCode;
